/**
 * Il LINK D'INVITO di un partner (03/11/2026, Simone: «Renato dalla sua console possa andare a invitare delle
 * persone … ci deve essere un codice ma non un nome, un cognome o neanche di un'azienda»).
 *
 * Un invito è un link `https://yumaos.com/invito#invito=<codice>` (o una pagina del sito con lo stesso segno in fondo,
 * `https://yumaos.com/it/video/fotovoltaico#invito=<codice>`): chi si iscrive a YumaOS da lì diventa un'azienda del
 * partner che l'ha creato. Il codice è di 8 caratteri CASUALI, senza niente che ricordi il partner: chi lo riceve
 * vede solo un invito a YumaOS.
 *
 * ## Perché il codice sta dopo il «#»
 * yumaos.com non ha riscritture: ogni pagina è un file e un indirizzo inesistente riceve la 404 vera (regola del sito,
 * controllata dal pre-rendering). La parte dopo il «#» non va mai al server né nelle anteprime dei link: il browser la
 * tiene per sé. Il sito la legge all'avvio, prima di disegnare e prima di qualunque codice di misura, tiene il codice e
 * la toglie dall'indirizzo (`marketing-site/src/signup/invitoPartner.ts`). E il link di un video mostra, su WhatsApp,
 * l'anteprima vera di quel video.
 *
 * Questo file è il contratto fra le tre parti che toccano il codice, e per questo sta in `shared/`:
 *  - partner.yumaos.com compone il link (`indirizzoInvito`);
 *  - yumaos.com lo legge dall'indirizzo (`codiceDalFrammento`), lo tiene finché la persona si iscrive e lo manda con
 *    l'iscrizione;
 *  - il server dell'iscrizione (codebase `marketplace`) scrive l'ARRIVO, e il server del programma partner (codebase
 *    `default`) lo abbina al partner (`functions/src/services/partner/inviti.ts`).
 *
 * ⚠️ Il codice NON è una credenziale: non apre niente, dice solo a chi va il merito di un'iscrizione. Per questo può
 * stare in un link che si inoltra. Il sito lo toglie comunque dall'indirizzo appena lo ha letto, e la pagina
 * dell'invito non carica i codici di misura (`MarketingTracking.tsx`).
 *
 * File puro, senza dipendenze: lo copiano anche i codebase satellite (`functions-marketplace/sync.sh`).
 */

/** Le lettere e le cifre dei codici: niente 0/O e 1/I, che al telefono si confondono. 32 simboli. */
export const ALFABETO_CODICE = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
/** 8 simboli su 32: oltre mille miliardi di combinazioni, un codice non si indovina provando. */
export const LUNGHEZZA_CODICE_INVITO = 8;

/** L'indirizzo del sito e il percorso della pagina dell'invito (la stessa costante usa il router del sito). */
export const INDIRIZZO_SITO = 'https://yumaos.com';
export const PERCORSO_INVITO = '/invito';
/** Il nome del segno in fondo al link: `#invito=<codice>`. */
export const SEGNO_INVITO = 'invito';

const FORMA = new RegExp(`^[${ALFABETO_CODICE}]{${LUNGHEZZA_CODICE_INVITO}}$`);

/**
 * Il codice in forma canonica (maiuscolo, senza spazi né trattini), o `null` se non ha la forma di un codice.
 * Accetta ciò che una persona potrebbe ricopiare a mano: «k7m2 qx9p» diventa «K7M2QX9P».
 */
export function normalizzaCodiceInvito(valore: unknown): string | null {
  if (typeof valore !== 'string') return null;
  const pulito = valore.replace(/[\s-]/g, '').toUpperCase();
  return FORMA.test(pulito) ? pulito : null;
}

/**
 * Una pagina del sito dove un link d'invito può portare (03/11/2026, Simone: il partner condivide anche i video del
 * fotovoltaico, non solo l'iscrizione). Solo percorsi DEL SITO, scritti come li scrive il sito: minuscole, cifre,
 * trattini e barre, con la lingua davanti (`/it/video/fotovoltaico`). Ammessi i parametri semplici con cui il sito apre
 * un video a un punto (`?video=capitolo-2&t=40`): nomi in minuscolo, valori di lettere, cifre e trattini. Niente domini,
 * niente `//`, niente `..`, niente caratteri speciali: il link non può portare fuori da yumaos.com.
 */
const DESTINAZIONE = /^\/(?:it|en)(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*\/?(?:\?[a-z_]{1,20}=[a-z0-9-]{1,80}(?:&[a-z_]{1,20}=[a-z0-9-]{1,80}){0,4})?$/;

export function destinazioneValida(percorso: unknown): percorso is string {
  return typeof percorso === 'string' && percorso.length <= 120 && DESTINAZIONE.test(percorso);
}

/**
 * Il link da mandare: `https://yumaos.com/invito#invito=K7M2QX9P` porta all'invito a iscriversi;
 * `https://yumaos.com/it/video/fotovoltaico#invito=K7M2QX9P` porta a quella pagina del sito, e il codice resta tenuto
 * per quando l'azienda si iscriverà.
 */
export function indirizzoInvito(codice: string, destinazione?: string | null): string {
  const pagina = destinazione && destinazioneValida(destinazione) ? destinazione : PERCORSO_INVITO;
  return `${INDIRIZZO_SITO}${pagina}#${SEGNO_INVITO}=${codice}`;
}

/** Il codice d'invito dalla parte dopo il «#» (`#invito=K7M2QX9P`), o `null` se non c'è o non ha la forma giusta. */
export function codiceDalFrammento(frammento: string | null | undefined): string | null {
  const testo = (frammento ?? '').replace(/^#/, '');
  for (const parte of testo.split('&')) {
    const [nome, valore] = parte.split('=');
    if (nome !== SEGNO_INVITO) continue;
    try {
      return normalizzaCodiceInvito(decodeURIComponent(valore ?? ''));
    } catch {
      return null; // una sequenza «%» rotta: non è un codice
    }
  }
  return null;
}

// ============================================================================
// L'ARRIVO: un'azienda si è iscritta con un codice d'invito
// ============================================================================

/**
 * `platform_partner_arrivi/{tenantId}`: lo scrive il server dell'iscrizione NELLO STESSO LOTTO dell'azienda (o nascono
 * tutti e due o nessuno), e lo legge il server del programma partner, che lo abbina e scrive l'esito. Uno per azienda:
 * l'id è quello dell'azienda. Raccolta di piattaforma, chiusa al browser (regola generale del super utente).
 */
export const RACCOLTA_ARRIVI = 'platform_partner_arrivi';

/**
 * Com'è andato l'abbinamento. Tutti gli esiti restano scritti: un'iscrizione arrivata da un link e non abbinata non
 * deve sparire in silenzio.
 *  - `abbinata`: l'azienda è del partner dell'invito (nuova, o già registrata da lui);
 *  - `di_altro_partner`: un altro partner l'aveva registrata prima e la sua registrazione vale ancora: resta sua;
 *  - `codice_sconosciuto`, `link_spento`, `partner_non_attivo`: il link non vale (più);
 *  - `azienda_del_partner`: il partner ha usato il suo link per la sua stessa azienda;
 *  - `partita_iva_non_valida`: la partita IVA dell'iscrizione non si può normalizzare.
 */
export type EsitoArrivo =
  | 'abbinata'
  | 'di_altro_partner'
  | 'codice_sconosciuto'
  | 'link_spento'
  | 'partner_non_attivo'
  | 'azienda_del_partner'
  | 'partita_iva_non_valida';

export interface ArrivoDaInvitoDoc {
  codice_invito: string;
  tenant_id: string;
  azienda_nome: string;
  /** Come la salva l'azienda (senza prefisso del paese). */
  partita_iva: string;
  /** Paese ISO a 2 lettere dell'azienda. */
  paese: string;
  /** Giorno dell'iscrizione, `AAAA-MM-GG` (UTC). */
  iscritta_il: string;
  /** `null` finché il server del programma partner non lo ha guardato. */
  esito: EsitoArrivo | null;
  partner_id?: string | null;
  abbinamento_id?: string | null;
  esaminato_il?: string | null;
  created_at?: unknown;
}
